/**
 * Offline banner — renders at top of layout when offline.
 * Auto-dismisses 3s after reconnection.
 * Rule 5 — uses useOnlineStatus(), never navigator.onLine directly.
 * Rule 2 — labels offline content as "Practice Bank", never "AI-generated".
 */
import { useEffect, useState }    from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { WifiOff, Wifi }           from 'lucide-react'
import { useOnlineStatus }         from '@/hooks/useOnlineStatus'

export function OfflineBanner() {
  const { isOnline, wasOffline, reconnectedAt } = useOnlineStatus()
  const [showReconnected, setShowReconnected]   = useState(false)

  useEffect(() => {
    if (!reconnectedAt) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShowReconnected(true)
    const timer = setTimeout(() => setShowReconnected(false), 3000)
    return () => clearTimeout(timer)
  }, [reconnectedAt])

  const showOffline     = !isOnline
  const showOnlineFlash = isOnline && wasOffline && showReconnected

  return (
    <AnimatePresence>
      {showOffline && (
        <motion.div
          key="offline"
          role="status"
          aria-live="polite"
          aria-label="You are offline. Using Practice Bank questions."
          initial={{ y: -48, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -48, opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="fixed top-0 inset-x-0 z-50 flex items-center justify-center gap-2
                     bg-amber-500 text-white text-sm font-medium py-2 px-4"
        >
          <WifiOff className="w-4 h-4 shrink-0" aria-hidden="true" />
          <span>You're offline — using Practice Bank questions</span>
        </motion.div>
      )}

      {showOnlineFlash && (
        <motion.div
          key="reconnected"
          role="status"
          aria-live="polite"
          aria-label="Back online. Syncing your progress."
          initial={{ y: -48, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -48, opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="fixed top-0 inset-x-0 z-50 flex items-center justify-center gap-2
                     bg-emerald-500 text-white text-sm font-medium py-2 px-4"
        >
          <Wifi className="w-4 h-4 shrink-0" aria-hidden="true" />
          <span>Back online — syncing your progress</span>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
